import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useResponsive } from '@/hooks/useResponsive';
import { cn } from '@/lib/utils';
import { ChevronLeft, ChevronRight } from 'lucide-react';

// Touch-optimized Input
interface TouchInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: { message?: string };
  required?: boolean;
}

export const TouchInput = React.forwardRef<HTMLInputElement, TouchInputProps>(({ 
  label, 
  error, 
  required = false, 
  className,
  ...props 
}, ref) => {
  const { isMobile } = useResponsive();
  
  return (
    <div className="space-y-2">
      {label !== undefined && (
        <Label className={cn(
          "font-medium",
          isMobile ? "text-base" : "text-sm"
        )}>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </Label>
      )}
      <Input 
        {...props}
        ref={ref}
        className={cn(
          "transition-all duration-200",
          isMobile ? "h-12 text-base" : "h-10 text-sm",
          error && "border-red-500 focus:border-red-500",
          className
        )}
      />
      {error && (
        <p className="text-sm text-red-500">{error.message}</p>
      )}
    </div>
  );
});
TouchInput.displayName = "TouchInput";

// Touch-optimized Textarea
interface TouchTextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: { message?: string };
  required?: boolean;
}

export const TouchTextarea = React.forwardRef<HTMLTextAreaElement, TouchTextareaProps>(({ 
  label, 
  error, 
  required = false, 
  className,
  ...props 
}, ref) => {
  const { isMobile } = useResponsive();
  
  return (
    <div className="space-y-2">
      {label !== undefined && (
        <Label className={cn(
          "font-medium",
          isMobile ? "text-base" : "text-sm"
        )}>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </Label>
      )}
      <Textarea 
        {...props}
        ref={ref}
        className={cn(
          "transition-all duration-200",
          isMobile ? "min-h-[100px] text-base" : "min-h-[80px] text-sm",
          error && "border-red-500 focus:border-red-500",
          className
        )}
      />
      {error && (
        <p className="text-sm text-red-500">{error.message}</p>
      )}
    </div>
  );
});
TouchTextarea.displayName = "TouchTextarea";

// Multi-step Form
export interface FormStep {
  id: string;
  title: string;
  description?: string;
  component: React.ReactNode;
  validation?: () => boolean | Promise<boolean>;
}

interface MultiStepFormProps {
  steps: FormStep[];
  /**
   * Dados do formulário para submissão (estrutura genérica)
   */
  onSubmit: (data: unknown) => void | Promise<void>;
  onStepChange?: (stepIndex: number) => void;
  className?: string;
  submitButtonText?: string;
  loading?: boolean;
}

export const MultiStepForm: React.FC<MultiStepFormProps> = ({
  steps,
  onSubmit,
  onStepChange,
  className = "",
  submitButtonText = "Finalizar",
  loading = false
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  // Estado genérico dos valores capturados em cada etapa
  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const { isMobile } = useResponsive();

  const handleNext = async () => {
    const step = steps[currentStep];
    
    // Validar step atual se houver validação
    if (step.validation) {
      const isValid = await step.validation();
      if (!isValid) return;
    }

    if (currentStep === steps.length - 1) {
      // Último step - submeter
      await onSubmit(formData);
    } else {
      // Próximo step
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      onStepChange?.(nextStep);
    }
  };

  const handlePrevious = () => {
    const prevStep = Math.max(0, currentStep - 1);
    setCurrentStep(prevStep);
    onStepChange?.(prevStep);
  };

  const progress = ((currentStep + 1) / steps.length) * 100;

  return (
    <Card className={cn("w-full max-w-2xl mx-auto", className)}>
      <CardHeader className="pb-4">
        <div className="flex justify-between items-center mb-4">
          <div>
            <CardTitle className={cn(
              isMobile ? "text-lg" : "text-xl"
            )}>
              {steps[currentStep].title}
            </CardTitle>
            {steps[currentStep].description && (
              <p className="text-sm text-gray-600 mt-1">
                {steps[currentStep].description}
              </p>
            )}
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-500">
              Passo {currentStep + 1} de {steps.length}
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-blue-500 h-2 rounded-full transition-all duration-300 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Step Indicators */}
        <div className="flex justify-between mt-4">
          {steps.map((step, index) => (
            <div
              key={step.id}
              className={cn(
                "flex items-center justify-center w-8 h-8 rounded-full text-xs font-medium transition-colors",
                index <= currentStep 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-200 text-gray-500'
              )}
            >
              {index + 1}
            </div>
          ))}
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        {/* Step Content */}
        <div className="mb-6">
          {steps[currentStep].component}
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between gap-4">
          <Button 
            variant="outline" 
            onClick={handlePrevious}
            disabled={currentStep === 0 || loading}
            className={cn(
              "flex items-center gap-2",
              isMobile ? "h-12 px-6" : "h-10 px-4"
            )}
          >
            <ChevronLeft className="w-4 h-4" />
            Anterior
          </Button>

          <Button 
            onClick={handleNext}
            disabled={loading}
            className={cn(
              "flex items-center gap-2",
              isMobile ? "h-12 px-6" : "h-10 px-4"
            )}
          >
            {currentStep === steps.length - 1 ? submitButtonText : 'Próximo'}
            {currentStep < steps.length - 1 && <ChevronRight className="w-4 h-4" />}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Responsive Form Container
interface ResponsiveFormProps {
  children: React.ReactNode;
  title?: string;
  description?: string;
  className?: string;
}

export const ResponsiveForm: React.FC<ResponsiveFormProps> = ({
  children,
  title,
  description,
  className = ""
}) => {
  const { isMobile } = useResponsive();

  if (isMobile) {
    return (
      <div className={cn("p-4", className)}>
        {title && (
          <div className="mb-6">
            <h1 className="text-xl font-bold text-gray-900 mb-2">{title}</h1>
            {description && (
              <p className="text-gray-600">{description}</p>
            )}
          </div>
        )}
        {children}
      </div>
    );
  }

  return (
    <Card className={cn("w-full max-w-2xl mx-auto", className)}>
      {title && (
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          {description && (
            <p className="text-gray-600">{description}</p>
          )}
        </CardHeader>
      )}
      <CardContent>
        {children}
      </CardContent>
    </Card>
  );
}; 